/**
 * Apply a diagonal "PROEF" watermark to every page of a jsPDF document.
 * Called after all pages have been added. Semi-transparent RED text at 45°
 * so it clearly signals "not for board use" without obscuring the numbers.
 *
 * @param {jsPDF} doc     - The jsPDF instance
 * @param {string} label  - The watermark text (default "PROEF")
 */
export function applyDiagonalWatermark(doc, label = "PROEF") {
  const total = doc.internal.getNumberOfPages();
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    // Preserve current text state
    const prevFontSize = doc.getFontSize?.();
    const prevGState = null;
    try {
      // Global alpha via GState (jsPDF 2.x)
      if (typeof doc.GState === "function" && typeof doc.setGState === "function") {
        const gs = new doc.GState({ opacity: 0.12 });
        doc.setGState(gs);
      }
      doc.setTextColor(200, 30, 30);
      doc.setFontSize(120);
      // Center + rotate around center
      doc.text(label, pw / 2, ph / 2, { align: "center", baseline: "middle", angle: 45 });
    } finally {
      // Reset to opaque so subsequent modifications aren't ghosted
      if (typeof doc.GState === "function" && typeof doc.setGState === "function") {
        const gs = new doc.GState({ opacity: 1 });
        doc.setGState(gs);
      }
      doc.setTextColor(0, 0, 0);
      if (prevFontSize) doc.setFontSize(prevFontSize);
    }
  }
}

/**
 * Convert a hex color like "#RRGGBB" to a `[r, g, b]` tuple usable by jsPDF's
 * `setDrawColor` / `setFillColor` / `setTextColor`. Falls back to the ClubFin
 * primary blue when the input is malformed.
 */
export function hexToRgb(hex) {
  const fallback = [10, 10, 10];
  if (!hex || typeof hex !== "string") return fallback;
  const m = hex.trim().match(/^#([0-9A-Fa-f]{6})$/);
  if (!m) return fallback;
  const int = parseInt(m[1], 16);
  return [(int >> 16) & 0xff, (int >> 8) & 0xff, int & 0xff];
}
